import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import { Button as BootstrapButton, Modal } from "react-bootstrap";

import { CUSTOMER_ACTIONS } from "_constants";
import { parseRequiredActions } from "_helpers";
import breakpoints from "_constants/breakpoints";

import { ordersActions } from "_store/actions";
import { requiredActionsActions } from "_store/actions";

import GeneralInfo from "components/OrderDetail/GeneralInfo";
import { ErrorModal } from "components/Modals/ErrorModal";
import { SpinnerWrapper } from "widget/Spinner";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 500px;

  animation: 0.2s linear fadeIn;
  @keyframes fadeIn {
    from {
      opacity: 0%;
    }
    to {
      opacity: 100%;
    }
  }
`;
const Title = styled.h2`
  margin-bottom: 14px;
  font-weight: bold;
  font-size: 24px;
  line-height: 47px;
  color: #03589c;

  @media (max-width: ${breakpoints.mobileM}) {
    margin-left: 0px;
    font-size: 18px;
  }
`;

const Info = styled.div`
  max-width: 1376px;
  width: 90%;
  padding: 12px 6px;
  text-align: justify;

  ${({ theme: { mobile } }) =>
    mobile
      ? css`
          padding: 0 16px 12px;

          @media (max-width: ${breakpoints.mobileM}) {
            font-size: 14px;
          }
        `
      : css`
          @media (max-width: 1350px) {
            width: 90%;
          }
        `}
`;

const Button = styled.button`
  max-width: 209px;
  width: 100%;
  height: 42px;
  margin-bottom: 24px;
  background: #f37217;
  border: none;
  border-radius: 4px;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  color: #fafaff;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  width: 100%;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: center;
    gap: 0;
  }
`;

const ModalText = styled.div`
  font-family: "Montserrat";

  ${({ textCenter }) =>
    textCenter &&
    css`
      width: 100%;
      text-align: center;
    `}
`;

const StyledModalFooter = styled(Modal.Footer)`
  justify-content: space-between;
`;

export default function RequiredActionDetails() {
  const [showAbandonModal, setShowAbandonModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orderDetails, orderDetailsLoading, error } = useSelector(({ orders }) => orders);
  const { requiredActions } = useSelector(({ requiredActions }) => requiredActions);

  const { orderId, actionId } = useParams();

  const actions = parseRequiredActions(requiredActions, orderDetails);
  const currentAction = actions?.find(({ id, type }) => id === actionId || type === actionId);
  const product = currentAction?.product;
  const productId = product?.id;

  function handlePurchase() {
    dispatch(
      ordersActions.customerAction({
        orderId,
        productId,
        actionType: CUSTOMER_ACTIONS.OAPurchase,
        notification: "An Office Action Response Service was purchased successfully.",
        cb: () => {
          dispatch(ordersActions.getOrder(orderId));
          dispatch(requiredActionsActions.getRequiredActions());
          navigate(`/orders/${orderId}`);
        },
      })
    );
  }

  function handleExternally() {
    dispatch(
      ordersActions.customerAction({
        orderId,
        productId,
        actionType: CUSTOMER_ACTIONS.OAHandleExternally,
        notification:
          "Your trademark order with us will remain active, and no further action is required from us at this time.",
        cb: () => {
          dispatch(ordersActions.getOrder(orderId));
          dispatch(requiredActionsActions.getRequiredActions());
          navigate(`/orders/${orderId}`);
        },
      })
    );
  }

  function handleAbandon() {
    dispatch(
      ordersActions.customerAction({
        orderId,
        productId,
        actionType: CUSTOMER_ACTIONS.OAAbandon,
        notification: "You have abandoned your trademark.",
        cb: () => {
          dispatch(ordersActions.getOrder(orderId));
          dispatch(requiredActionsActions.getRequiredActions());
          navigate(`/orders/${orderId}`);
        },
      })
    );
  }

  useEffect(() => dispatch(ordersActions.getOrder(orderId)), [orderId, dispatch]);

  useEffect(() => {
    if (productId) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      });
    }
  }, [productId]);

  if (!orderDetails || orderDetailsLoading)
    return (
      <Container key="container-speciman-alert">
        {(orderDetailsLoading && <SpinnerWrapper />) || (error && <ErrorModal />)}
      </Container>
    );

  if (!productId) {
    navigate(`/orders/${orderId}`);

    return null;
  }

  return (
    <>
      <Container key="container-required-action-details">
        <GeneralInfo orderDetails={orderDetails} />
        <Title>Office Action Received</Title>
        <Info>
          The United States Patent & Trademark Office (USPTO) has issued an Office Action for your trademark
          application. An Office Action is a document sent by the USPTO when the examiner needs clarification,
          additional information, or identifies issues that must be addressed before your application can move forward.
        </Info>
        <Info>
          Our legal team has reviewed the type of Office Action you received, and we are able to assist with preparing a
          response. If you’d like us to handle this for you, we offer an Office Action Response Service for <b>$199</b>.
        </Info>
        <Info>
          The USPTO generally allows 3 months from the date the Office Action is issued to submit a response. To ensure
          our legal team has enough time to prepare and file your response, please complete your purchase in your Member
          Portal no later than 2.5 months from the Office Action issuance date.
        </Info>
        <Info>
          If you prefer, you may also choose to handle the response with your own trademark attorney or decide not to
          move forward with the application.
        </Info>
        <ActionButtonsContainer>
          <Button disabled={orderDetailsLoading} onClick={handlePurchase}>
            Purchase
          </Button>
          <Button disabled={orderDetailsLoading} onClick={handleExternally}>
            Handle externally
          </Button>
          <Button disabled={orderDetailsLoading} onClick={() => setShowAbandonModal(true)}>
            Abandon Trademark
          </Button>
        </ActionButtonsContainer>
        <Button onClick={() => navigate(`/orders/${orderId}`)}>Go Back</Button>
      </Container>
      <Modal centered show={showAbandonModal} size="md" backdrop="static" onHide={() => setShowAbandonModal(false)}>
        <Modal.Header closeButton>
          <ModalText>Abandon Trademark</ModalText>
        </Modal.Header>
        <Modal.Body>
          <ModalText>Are you sure you would like to Abandon your trademark?</ModalText>
        </Modal.Body>
        <StyledModalFooter>
          <BootstrapButton
            type="button"
            variant="success"
            onClick={() => {
              handleAbandon();
              setShowAbandonModal(false);
            }}
          >
            Yes
          </BootstrapButton>
          <BootstrapButton
            type="button"
            variant="secondary"
            onClick={() => {
              setShowAbandonModal(false);
            }}
          >
            No
          </BootstrapButton>
        </StyledModalFooter>
      </Modal>
    </>
  );
}
